import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import {
  ImageCompressionSlider,
  compressImageFile,
} from "@/components/image-compression-slider";
import { SiteHeader } from "@/components/site-header";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCanWriteHere } from "@/contexts/area.context";
import { formatBytes, formatDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import { ApiError } from "@/providers/base.provider";
import {
  storageProvider,
  type StorageObject,
} from "@/providers/storage.provider";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconCopy,
  IconFolder,
  IconLayoutGrid,
  IconList,
  IconLoader2,
  IconPhoto,
  IconRefresh,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// The hard cap the signed upload policy enforces — what actually gets
// uploaded must land under this after compression.
const MAX_UPLOAD_SIZE = 250 * 1024;
// A looser cap on what can be picked at all, since compression still has to
// bring it down from there.
const MAX_PICK_SIZE = 5 * 1024 * 1024;

// ── Helpers ──────────────────────────────────────────────────────────────────

// The backend is documented to return bare folder names in `dirs`, but
// defend against it echoing the full relative path (e.g. "promo/packs" while
// browsing "promo") — either way we only want the last segment.
function dirName(prefix: string, dir: string): string {
  const stripped = prefix && dir.startsWith(`${prefix}/`) ? dir.slice(prefix.length + 1) : dir;
  return stripped.split("/").filter(Boolean).pop() ?? stripped;
}

function listErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 502) {
    return "Couldn't load files, try again.";
  }
  return "Couldn't load files, try again.";
}

class CompressedTooLargeError extends Error {}

function uploadErrorMessage(error: unknown): string {
  if (error instanceof CompressedTooLargeError) return error.message;
  if (error instanceof ApiError && error.status === 400) {
    return "Please use some letters or numbers in the name.";
  }
  return "Upload failed, please try again.";
}

function deleteErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    return "Couldn't delete that file.";
  }
  return "Couldn't delete that file. Try again.";
}

function parentPrefix(key: string): string {
  const idx = key.lastIndexOf("/");
  return idx === -1 ? "" : key.slice(0, idx);
}

// ── Copy URL button ──────────────────────────────────────────────────────────

function CopyUrlButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("URL copied");
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-9 sm:size-7"
      onClick={handleCopy}
      title="Copy URL"
    >
      {copied ? (
        <IconCheck className="size-3.5 text-green-500" />
      ) : (
        <IconCopy className="size-3.5" />
      )}
    </Button>
  );
}

// ── Delete file ──────────────────────────────────────────────────────────────

function DeleteFileAction({
  file,
  listPrefix,
}: {
  file: StorageObject;
  /** Whichever prefix the current view (browse or flat list) is showing. */
  listPrefix: string;
}) {
  const qc = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: () => storageProvider.delete(file.key),
    onSuccess: () => {
      // The file's own folder listing (browse view) and the active flat
      // listing can be different prefixes — invalidate both.
      qc.invalidateQueries({
        queryKey: queryKeys.adminStorageList(parentPrefix(file.key)),
      });
      qc.invalidateQueries({ queryKey: ["admin-storage-list-all", listPrefix] });
      toast.success("File deleted");
    },
    onError: (err) => toast.error(deleteErrorMessage(err)),
  });

  return (
    <ConfirmDestructiveDialog
      confirmValue={file.key}
      confirmLabel="file key"
      title={`Delete "${file.key}"?`}
      description="This can't be undone."
      actionLabel="Delete file"
      onConfirm={() => deleteMut.mutate()}
      isPending={deleteMut.isPending}
      trigger={({ disabled }) => (
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-destructive hover:text-destructive sm:size-7"
          disabled={disabled}
          title="Delete"
        >
          <IconTrash className="size-3.5" />
        </Button>
      )}
    >
      <img
        src={file.url}
        alt={file.key}
        className="max-h-48 w-full rounded-lg border bg-muted object-contain"
      />
    </ConfirmDestructiveDialog>
  );
}

// ── Upload dialog ────────────────────────────────────────────────────────────

function UploadDialog({
  open,
  onClose,
  prefix,
}: {
  open: boolean;
  onClose: () => void;
  prefix: string;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(85);
  const [destPrefix, setDestPrefix] = useState(prefix);
  const [filename, setFilename] = useState("");

  // The dialog stays mounted across opens, so the current folder can change
  // between one open and the next — resync the default every time it opens.
  useEffect(() => {
    if (open) setDestPrefix(prefix);
  }, [open, prefix]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const compressed = await compressImageFile(file!, quality);
      if (compressed.size > MAX_UPLOAD_SIZE) {
        throw new CompressedTooLargeError(
          "Still over 250 KB after compression — lower the quality and try again.",
        );
      }
      const uploadUrl = await storageProvider.getUploadUrl(
        destPrefix,
        filename.trim() || compressed.name,
      );
      await storageProvider.uploadToPresignedUrl(uploadUrl, compressed);
      return uploadUrl;
    },
    onSuccess: (uploadUrl) => {
      // Use the actual stored key, not the typed destPrefix — normalization
      // can change it. Refresh the folder it landed in, that folder's parent
      // (a brand-new folder needs to show up as a tile there too), and
      // whatever prefix is currently being viewed, across both browse and
      // flat-list caches.
      const finalPrefix = parentPrefix(uploadUrl.key);
      const prefixesToRefresh = Array.from(
        new Set([prefix, finalPrefix, parentPrefix(finalPrefix)]),
      );
      prefixesToRefresh.forEach((p) => {
        qc.invalidateQueries({ queryKey: queryKeys.adminStorageList(p) });
        qc.invalidateQueries({ queryKey: ["admin-storage-list-all", p] });
      });
      toast.success(`Uploaded as ${uploadUrl.key}`);
      resetAndClose();
    },
    onError: (err) => toast.error(uploadErrorMessage(err)),
  });

  function resetAndClose() {
    setFile(null);
    setQuality(85);
    setDestPrefix(prefix);
    setFilename("");
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  function handleFilePick(picked: File | undefined) {
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    if (picked.size > MAX_PICK_SIZE) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setFile(picked);
    setQuality(85);
    // Prefill with the picked file's name (extension included) so retyping
    // it means editing, not remembering the extension from scratch.
    setFilename(picked.name);
  }

  const canSubmit = file !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div
              className="flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border bg-muted transition-opacity hover:opacity-80"
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt="preview"
                  className="size-full object-cover"
                />
              ) : (
                <IconPhoto className="size-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                className="gap-1.5"
              >
                <IconUpload className="size-3.5" />
                {file ? "Change file" : "Choose file"}
              </Button>
              {file && (
                <button
                  type="button"
                  className="text-left text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    setFile(null);
                    setFilename("");
                  }}
                >
                  Remove
                </button>
              )}
              <p className="text-xs text-muted-foreground">
                Image, max 5 MB — compressed to fit under 250 KB before upload
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                handleFilePick(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>

          {file && (
            <ImageCompressionSlider
              file={file}
              quality={quality}
              onChange={setQuality}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-folder">Folder</Label>
            <Input
              id="upload-folder"
              placeholder="e.g. banners"
              value={destPrefix}
              onChange={(e) => setDestPrefix(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-filename">Filename</Label>
            <Input
              id="upload-filename"
              placeholder="e.g. hero.jpg"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Prefilled from the chosen file, extension included — edit freely.
              Names are normalized to snake_case, so the final name may still
              differ from what you type here.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={isPending || !canSubmit}>
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconUpload className="size-4" />
            )}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tiles ────────────────────────────────────────────────────────────────────

function FolderTile({ name, onOpen }: { name: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-w-0 flex-col items-center gap-2 rounded-lg border p-3 text-center transition-colors hover:bg-muted"
    >
      <IconFolder className="size-10 shrink-0 text-muted-foreground" />
      <span className="w-full truncate text-xs font-medium">{name}</span>
    </button>
  );
}

function FileTile({
  file,
  listPrefix,
}: {
  file: StorageObject;
  listPrefix: string;
}) {
  const canWrite = useCanWriteHere();
  const name = file.key.split("/").pop() ?? file.key;

  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border p-3">
      <img
        src={file.url}
        alt={name}
        className="aspect-square w-full rounded border bg-muted object-cover"
      />
      <div className="min-w-0">
        <p className="truncate text-xs font-medium" title={name}>
          {name}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {formatBytes(file.size)} · {formatDateTime(file.last_modified)}
        </p>
      </div>
      <div className="flex items-center justify-end gap-1">
        <CopyUrlButton url={file.url} />
        {canWrite && <DeleteFileAction file={file} listPrefix={listPrefix} />}
      </div>
    </div>
  );
}

// ── Flat list row ────────────────────────────────────────────────────────────

function FlatFileRow({
  file,
  listPrefix,
}: {
  file: StorageObject;
  listPrefix: string;
}) {
  const canWrite = useCanWriteHere();

  return (
    <TableRow>
      <TableCell>
        <img
          src={file.url}
          alt={file.key}
          className="size-8 shrink-0 rounded border bg-muted object-cover"
        />
      </TableCell>
      <TableCell className="max-w-0 min-w-[10rem]">
        <p className="truncate font-mono text-xs" title={file.key}>
          {file.key}
        </p>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {formatBytes(file.size)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {formatDateTime(file.last_modified)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <CopyUrlButton url={file.url} />
          {canWrite && (
            <DeleteFileAction file={file} listPrefix={listPrefix} />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

type ViewMode = "browse" | "list";

export function StoragePage() {
  const [prefix, setPrefix] = useState("");
  const [view, setView] = useState<ViewMode>("browse");
  const [uploadOpen, setUploadOpen] = useState(false);
  const canWrite = useCanWriteHere();

  // Flat-list pagination is cursor-based (S3 StartAfter) rather than
  // page-numbered, so "back" means replaying the stack of tokens that got us
  // to the current page.
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>(
    [],
  );

  const browseQuery = useQuery({
    queryKey: queryKeys.adminStorageList(prefix),
    queryFn: () => storageProvider.list(prefix),
    staleTime: 30_000,
    enabled: view === "browse",
  });

  const listAllQuery = useQuery({
    queryKey: queryKeys.adminStorageListAll(prefix, pageToken ?? ""),
    queryFn: () => storageProvider.listAll(prefix, pageToken),
    staleTime: 30_000,
    enabled: view === "list",
  });

  const dirs = browseQuery.data?.dirs ?? [];
  const files = browseQuery.data?.objects ?? [];
  const flatFiles = listAllQuery.data?.objects ?? [];
  const hasNextPage = !!listAllQuery.data?.next_page_token;
  const hasPrevPage = tokenHistory.length > 0;
  const segments = prefix ? prefix.split("/").filter(Boolean) : [];

  function resetPagination() {
    setPageToken(undefined);
    setTokenHistory([]);
  }

  function goToPrefix(next: string) {
    setPrefix(next);
    resetPagination();
  }

  function changeView(next: ViewMode) {
    setView(next);
    resetPagination();
  }

  function navigateTo(depth: number) {
    goToPrefix(segments.slice(0, depth).join("/"));
  }

  function goNextPage() {
    if (!listAllQuery.data?.next_page_token) return;
    setTokenHistory((h) => [...h, pageToken]);
    setPageToken(listAllQuery.data!.next_page_token);
  }

  function goPrevPage() {
    setTokenHistory((h) => {
      if (h.length === 0) return h;
      setPageToken(h[h.length - 1]);
      return h.slice(0, -1);
    });
  }

  const isFetching =
    view === "browse" ? browseQuery.isFetching : listAllQuery.isFetching;

  function refetch() {
    return view === "browse" ? browseQuery.refetch() : listAllQuery.refetch();
  }

  return (
    <div className="flex h-full flex-col">
      <SiteHeader title="Storage" />
      <div className="flex-1 overflow-auto px-4 pb-6 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 py-4 lg:py-6">
          <Breadcrumb className="min-w-0">
            <BreadcrumbList className="flex-nowrap overflow-x-auto no-scrollbar">
              <BreadcrumbItem>
                {segments.length === 0 ? (
                  <BreadcrumbPage>Home</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <button type="button" onClick={() => navigateTo(0)}>
                      Home
                    </button>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {segments.map((segment, i) => (
                <span key={i} className="flex items-center gap-1.5 sm:gap-2.5">
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {i === segments.length - 1 ? (
                      <BreadcrumbPage className="max-w-[10rem] truncate">
                        {segment}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <button
                          type="button"
                          className="max-w-[10rem] truncate"
                          onClick={() => navigateTo(i + 1)}
                        >
                          {segment}
                        </button>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex shrink-0 items-center gap-2">
            <div className="flex items-center rounded-md border p-0.5">
              <Button
                variant={view === "browse" ? "secondary" : "ghost"}
                size="icon"
                className="size-9 sm:size-7"
                onClick={() => changeView("browse")}
                title="Browse folders"
              >
                <IconLayoutGrid className="size-4" />
              </Button>
              <Button
                variant={view === "list" ? "secondary" : "ghost"}
                size="icon"
                className="size-9 sm:size-7"
                onClick={() => changeView("list")}
                title="List all files"
              >
                <IconList className="size-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="size-9 sm:size-8"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh"
            >
              <IconRefresh
                className={`size-4 ${isFetching ? "animate-spin" : ""}`}
              />
            </Button>
            {canWrite && (
              <Button
                size="sm"
                onClick={() => setUploadOpen(true)}
                className="gap-1.5"
              >
                <IconUpload className="size-4" />
                Upload
              </Button>
            )}
          </div>
        </div>

        {view === "browse" ? (
          browseQuery.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-lg" />
              ))}
            </div>
          ) : browseQuery.isError ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {listErrorMessage(browseQuery.error)}
            </div>
          ) : dirs.length === 0 && files.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {prefix
                ? "This folder is empty."
                : "No files yet — upload one to get started."}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {dirs.map((dir) => {
                const name = dirName(prefix, dir);
                return (
                  <FolderTile
                    key={dir}
                    name={name}
                    onOpen={() =>
                      goToPrefix(prefix ? `${prefix}/${name}` : name)
                    }
                  />
                );
              })}
              {files.map((file) => (
                <FileTile key={file.key} file={file} listPrefix={prefix} />
              ))}
            </div>
          )
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-muted-foreground">
              Every file under {prefix ? `"${prefix}"` : "the root"},
              recursively — folder nesting is flattened.
            </p>

            {listAllQuery.isLoading ? (
              <div className="rounded-lg border">
                <Table>
                  <TableBody>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : listAllQuery.isError ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {listErrorMessage(listAllQuery.error)}
              </div>
            ) : flatFiles.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {prefix
                  ? "No files under this folder."
                  : "No files yet — upload one to get started."}
              </div>
            ) : (
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead />
                      <TableHead>Key</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Modified</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flatFiles.map((file) => (
                      <FlatFileRow
                        key={file.key}
                        file={file}
                        listPrefix={prefix}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {(hasPrevPage || hasNextPage) && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrevPage || listAllQuery.isFetching}
                  onClick={goPrevPage}
                  className="gap-1"
                >
                  <IconChevronLeft className="size-3.5" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNextPage || listAllQuery.isFetching}
                  onClick={goNextPage}
                  className="gap-1"
                >
                  Next
                  <IconChevronRight className="size-3.5" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        prefix={prefix}
      />
    </div>
  );
}
